import React from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { CURRICULUM_OPTIONS, CURRICULUM_STANDARD_VALUES } from "@/lib/taxonomy";

/**
 * CurriculumPicker — pure canonical multi-select for the school `curriculum`
 * field. Only values from `CURRICULUM_OPTIONS` are ever stored; free-text
 * specifics (e.g. "Cambridge Lower Secondary & Kurikulum Merdeka") live in the
 * separate `curriculum_detail` string field rendered next to this picker in
 * the Admin form.
 *
 * Legacy off-list values from before the Feb 2026 canonicalization are gracefully
 * hidden from the checkbox grid but silently preserved through `onChange` so a
 * pre-migration doc can be opened, edited on other fields, and saved without
 * data loss. (Post-migration this branch is dead code in practice.)
 *
 * @param {string[]} value Current curriculum array (canonical values only after migration)
 * @param {(next:string[])=>void} onChange
 * @param {string} testidPrefix
 */
export default function CurriculumPicker({ value = [], onChange, testidPrefix = "curriculum" }) {
  const selected = new Set(value || []);

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };

  // Preserve any legacy (pre-migration) off-canon values so save doesn't drop them.
  const legacyExtras = (value || []).filter((v) => !CURRICULUM_STANDARD_VALUES.includes(v));

  return (
    <div data-testid={testidPrefix}>
      <div className="text-xs uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2">
        Kurikulum (bisa pilih lebih dari satu)
      </div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-4">
        {CURRICULUM_OPTIONS.map((opt) => {
          const id = `${testidPrefix}-${opt.value}`;
          const checked = selected.has(opt.value);
          return (
            <label key={opt.value} htmlFor={id} className="flex items-center gap-2 cursor-pointer select-none group">
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={() => toggle(opt.value)}
                data-testid={`${testidPrefix}-checkbox-${opt.value}`}
                className="border-[#1E3A8A] data-[state=checked]:bg-[#1E3A8A] data-[state=checked]:text-white"
              />
              <span className={"text-sm text-[#1E3A8A] group-hover:text-[#1E40AF] transition-colors " + (checked ? "font-semibold" : "font-medium")}>
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
      {legacyExtras.length > 0 && (
        <div className="mt-3 text-[11px] text-[#7C2D12] bg-[#FEF3C7] rounded-md px-2 py-1" data-testid={`${testidPrefix}-legacy-warn`}>
          Nilai lama yang belum dipetakan (tetap disimpan): {legacyExtras.join(", ")}
        </div>
      )}
    </div>
  );
}
